import { Helmet } from "react-helmet-async";
import { useSeasonContext } from "@/contexts/SeasonContext";
import { SeasonProvider } from "@/contexts/SeasonContext";
import { Home, UtensilsCrossed } from "lucide-react";

const NotFoundContent = () => {
  const { isSummer } = useSeasonContext();

  // Alignement sur votre charte graphique existante
  const accentTextClass = isSummer ? "text-summer-accent" : "text-ochre";
  const btnClass = isSummer ? "bg-summer-accent hover:bg-summer-accent-dark" : "bg-ochre hover:bg-ochre-dark";

  return (
    <div className="flex min-h-screen items-center justify-center bg-sand/30 px-6">
      <div className="max-w-md w-full text-center">
        {/* Élément visuel stylisé */}
        <div className="mb-8 flex justify-center">
          <div className={`relative ${isSummer ? "text-summer-accent/20" : "text-ochre/20"}`}>
            <span className="text-[12rem] font-serif font-bold leading-none">404</span>
            <div className="absolute inset-0 flex items-center justify-center">
              <UtensilsCrossed size={60} className={`${accentTextClass} opacity-100`} />
            </div>
          </div>
        </div>

        <h1 className="font-serif text-3xl md:text-4xl text-foreground mb-4">Une petite erreur de parcours...</h1>

        <p className="text-muted-foreground text-lg mb-10 leading-relaxed">
          Il semble que la page que vous cherchez ait quitté la table. L'Ostréa vous invite à retrouver le chemin du
          goût.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="/"
            className={`flex items-center gap-2 px-8 py-3 text-white font-medium rounded-lg transition-all duration-300 shadow-soft hover:shadow-medium transform hover:-translate-y-0.5 ${btnClass}`}
          >
            <Home size={18} />
            Retour à l'accueil
          </a>

          <a
            href="/#menu"
            className="text-foreground/70 hover:text-foreground font-medium transition-colors duration-300 flex items-center gap-2"
          >
            Découvrir notre carte
          </a>
        </div>

        {/* Petit détail de marque en bas */}
        <div className="mt-16">
          <div className={`w-12 h-0.5 ${isSummer ? "bg-summer-accent/30" : "bg-ochre/30"} mx-auto mb-4`} />
          <p className={`${accentTextClass} font-medium text-xs uppercase tracking-[0.2em]`}>L'Ostréa Mazères</p>
        </div>
      </div>
    </div>
  );
};

const NotFound = () => {
  return (
    <SeasonProvider>
      <Helmet>
        <title>Page non trouvée — L'Ostréa Dégustation</title>
        <meta
          name="description"
          content="La page demandée est introuvable. Retournez à l'accueil du restaurant L'Ostréa à Mazères pour découvrir notre carte et nos actualités."
        />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://restaurant-lostrea-mazeres.fr/404" />
        <meta property="og:title" content="Page non trouvée — L'Ostréa Dégustation" />
        <meta
          property="og:description"
          content="Cette page n'existe pas ou n'existe plus. Revenez à l'accueil du restaurant L'Ostréa à Mazères."
        />
        <meta property="og:url" content="https://restaurant-lostrea-mazeres.fr/404" />
        <meta name="twitter:title" content="Page non trouvée — L'Ostréa Dégustation" />
        <meta
          name="twitter:description"
          content="Cette page n'existe pas ou n'existe plus. Revenez à l'accueil du restaurant L'Ostréa à Mazères."
        />
      </Helmet>
      <NotFoundContent />
    </SeasonProvider>
  );
};

export default NotFound;
